import { useMutation, useQueryClient } from "@tanstack/react-query";
import { facilityApi } from "@bw/shared/api";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Remove a guest from a booking's roster (DELETE /facilities/{facilityId}/
 * bookings/{bookingId}/participants/{userId} → 204/void). The backend 409s if the
 * guest has already checked in, so the UI never offers an enabled Remove on a
 * checked-in row.
 *
 * On success the roster query is INVALIDATED so it re-reads the server's truth —
 * never an optimistic local removal. A failure surfaces on `mutation.error` for
 * an honest message (no fake removal).
 */
export function useRemoveBookingParticipant(
  facilityId: string | undefined,
  bookingId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (userId: string) =>
      facilityApi.removeBookingParticipant(
        facilityId as string,
        bookingId as string,
        userId,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.booking.participants(
          facilityId ?? "",
          bookingId ?? "",
        ),
      });
    },
  });
}
